import React from 'react';
import { ExternalLink, Globe } from 'lucide-react';
import { useLinkPreview } from '@/hooks/useLinkPreview';
import { websiteFaviconUrl } from '@/lib/entityMedia';

/**
 * Rich OG link-preview card. Renders whichever meta we successfully scraped
 * (title, description, hero image, site name, favicon) as a clickable card
 * that opens the target site in a new tab.
 *
 * Use this on entity detail pages where a partner has a website but no
 * uploaded profile media — it gives readers real editorial context (the
 * site's own title & description) instead of just a raw screenshot.
 */
export const LinkPreviewCard = ({ url, className = '' }) => {
  const { data, loading } = useLinkPreview(url);

  if (!url) return null;

  if (loading) {
    return (
      <div
        data-testid="link-preview-loading"
        className={`animate-pulse rounded-xl border border-neutral-200 bg-neutral-50 p-4 ${className}`}
      >
        <div className="h-40 w-full rounded-md bg-neutral-200" />
        <div className="mt-3 h-4 w-2/3 rounded bg-neutral-200" />
        <div className="mt-2 h-3 w-full rounded bg-neutral-200" />
      </div>
    );
  }

  const title = data?.title;
  const description = data?.description;
  const image = data?.image;
  const siteName = data?.site_name;
  const favicon = data?.favicon || websiteFaviconUrl(url);

  let hostname = '';
  try {
    hostname = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    hostname = url;
  }

  // No useful metadata at all — render a minimal "visit website" chip so the
  // link is still discoverable.
  if (!title && !description && !image) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="link-preview-fallback"
        className={`inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-700 transition hover:border-neutral-400 hover:bg-neutral-50 ${className}`}
      >
        {favicon ? (
          <img src={favicon} alt="" className="h-4 w-4 rounded" />
        ) : (
          <Globe className="h-4 w-4" />
        )}
        <span className="truncate">{hostname}</span>
        <ExternalLink className="h-3.5 w-3.5 text-neutral-400" />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="link-preview-card"
      className={`group block overflow-hidden rounded-xl border border-neutral-200 bg-white transition hover:border-neutral-400 hover:shadow-md ${className}`}
    >
      {image && (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-neutral-100">
          <img
            src={image}
            alt={title || hostname}
            data-testid="link-preview-image"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="flex items-start gap-3 p-4">
        {favicon && (
          <img
            src={favicon}
            alt=""
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-neutral-500">
            <span className="truncate" data-testid="link-preview-hostname">
              {siteName || hostname}
            </span>
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </div>
          {title && (
            <div
              className="line-clamp-2 text-base font-semibold text-neutral-900"
              data-testid="link-preview-title"
            >
              {title}
            </div>
          )}
          {description && (
            <div
              className="mt-1 line-clamp-2 text-sm text-neutral-600"
              data-testid="link-preview-description"
            >
              {description}
            </div>
          )}
        </div>
      </div>
    </a>
  );
};

export default LinkPreviewCard;
